import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import ClientForm from "@/components/ClientForm";
import WorkspaceTab from "@/components/WorkspaceTab";
import MeetingsTab from "@/components/MeetingsTab";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Pencil, Mail, Phone, User as UserIcon, MapPin, Star, Coins, Trash2, Building2, Hash, Compass, FileDigit, Scale, Landmark, ShieldCheck, Check, AlertTriangle, FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const vatCls = (s) => (s === "Czynny" ? "text-emerald-700 font-semibold" : s === "Zwolniony" ? "text-amber-600 font-semibold" : "text-red-600 font-semibold");

const SUB_STARS = { None: 0, Standard: 2, Premium: 3 };
const gradeOf = (v) => (v >= 200000 ? { label: "Gold", cls: "bg-[#C99734] text-white" } : v >= 50000 ? { label: "Silver", cls: "bg-zinc-300 text-zinc-800" } : v > 0 ? { label: "Bronze", cls: "bg-amber-700 text-white" } : { label: "—", cls: "bg-zinc-100 text-zinc-500" });
const plnFmt = (v) => new Intl.NumberFormat("pl-PL", { style: "currency", currency: "PLN", maximumFractionDigits: 0 }).format(v || 0);

const statusStyles = {
  Lead: "bg-amber-100 text-amber-700",
  Active: "bg-emerald-100 text-emerald-700",
  Inactive: "bg-zinc-100 text-zinc-500",
};

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <Icon className="h-4 w-4 text-zinc-400 mt-0.5 shrink-0" />
    <div>
      <p className="text-xs text-zinc-400 font-[Manrope] uppercase tracking-wide">{label}</p>
      <p className="font-[Manrope] text-sm mt-0.5">{value || "—"}</p>
    </div>
  </div>
);

function RevenuePanel({ clientId, canInvoice }) {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], ltv: 0, trend: [] });
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [desc, setDesc] = useState("");
  const load = () => api.get(`/clients/${clientId}/transactions`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);
  const add = async () => {
    const a = parseFloat(amount);
    if (!a) return;
    await api.post(`/clients/${clientId}/transactions`, { amount: a, date, description: desc });
    setAmount(""); setDesc(""); load();
  };
  const del = async (id) => { await api.delete(`/clients/${clientId}/transactions/${id}`); load(); };
  const open = data.items.filter((t) => !t.invoice_id);
  const toProforma = async () => {
    try {
      const r = await api.post("/invoices/from-transactions", { client_id: clientId });
      toast.success("Utworzono szkic proformy z transakcji");
      navigate(`/invoices?edit=${r.data.invoice_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Błąd"); }
  };
  const max = Math.max(1, ...data.trend.map((t) => t.total));
  return (
    <div className="mt-6 border border-border bg-white rounded-md p-6" data-testid="revenue-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-[Outfit] font-semibold tracking-tight">Rejestr przychodów</h3>
        <div className="text-right">
          <p className="text-xs text-zinc-400 font-[Manrope] uppercase tracking-wide">Wartość życiowa (LTV)</p>
          <p className="font-[Outfit] font-bold text-2xl text-[#193962]" data-testid="ltv-value">{plnFmt(data.ltv)}</p>
        </div>
      </div>
      {data.trend.length > 0 && (
        <div className="mt-5 flex items-end gap-2 h-28" data-testid="revenue-trend">
          {data.trend.map((t) => (
            <div key={t.month} className="flex-1 flex flex-col items-center justify-end">
              <div className="w-full bg-[#C99734] rounded-t" style={{ height: `${Math.round((t.total / max) * 96)}px` }} title={plnFmt(t.total)} />
              <span className="text-[10px] text-zinc-400 font-[Manrope] mt-1">{t.month}</span>
            </div>
          ))}
        </div>
      )}
      <div className="mt-5 flex items-end gap-2 flex-wrap">
        <Input type="number" data-testid="txn-amount" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Kwota (PLN)" className="w-36" />
        <Input type="date" data-testid="txn-date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40" />
        <Input data-testid="txn-desc" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Opis" className="flex-1 min-w-[140px]" />
        <Button onClick={add} data-testid="txn-add" className="bg-[#193962] hover:bg-[#12294A] text-white">Dodaj</Button>
        {canInvoice && open.length > 0 && (
          <Button variant="outline" onClick={toProforma} data-testid="txn-to-proforma">Proforma z transakcji ({open.length})</Button>
        )}
      </div>
      <div className="mt-4 divide-y divide-border">
        {data.items.length === 0 ? <p className="py-4 text-sm text-muted-foreground font-[Manrope]">Brak transakcji.</p> :
          data.items.slice().reverse().map((t) => (
            <div key={t.txn_id} className="flex items-center gap-3 py-2" data-testid={`txn-${t.txn_id}`}>
              <span className="font-[Outfit] font-semibold text-sm w-28">{plnFmt(t.amount)}</span>
              <span className="text-xs text-zinc-400 font-[Manrope] w-24">{t.date}</span>
              <span className="text-sm font-[Manrope] flex-1 truncate">{t.description || "—"}</span>
              {t.invoice_id && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700" data-testid={`txn-invoiced-${t.txn_id}`}>zafakturowano</span>}
              <button onClick={() => del(t.txn_id)} className="text-zinc-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
      </div>
    </div>
  );
}

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [wl, setWl] = useState(null);
  const [wlLoading, setWlLoading] = useState(false);

  const verifyWL = async () => {
    const nip = (client.nip || "").replace(/\D/g, "");
    if (nip.length !== 10) { toast.error("Brak lub nieprawidłowy NIP klienta"); return; }
    setWlLoading(true);
    try {
      const acct = (client.bank_account || "").replace(/\D/g, "");
      const { data } = await api.get(`/nip/${nip}/verify`, { params: { account: acct, client_id: client.client_id } });
      setWl({ ...data, checked_at: new Date() });
      if (!data.found) toast.warning("Nie znaleziono firmy w rejestrze MF");
      else toast.success("Zweryfikowano na Białej Liście");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Błąd weryfikacji");
    } finally {
      setWlLoading(false);
    }
  };

  const load = () => {
    api.get(`/clients/${id}`).then((r) => setClient(r.data)).catch(() => navigate("/clients")).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (loading || !client) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Navbar />
        <div className="max-w-6xl mx-auto px-6 py-10 font-[Manrope] text-muted-foreground">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 py-8">
        <button onClick={() => navigate("/clients")} data-testid="back-to-clients" className="flex items-center gap-2 text-sm text-muted-foreground font-[Manrope] hover:text-zinc-900 transition-colors mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to clients
        </button>

        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-[Outfit] font-bold tracking-tight text-4xl" data-testid="client-detail-name">{client.company_name}</h1>
              <span className={`rounded-full text-xs px-2.5 py-0.5 font-[Manrope] font-medium ${statusStyles[client.status]}`}>{client.status}</span>
              {client.unsubscribed && <span data-testid="client-unsubscribed-badge" title="Ten kontakt zrezygnował z mailingów PATH" className="rounded-full text-xs px-2.5 py-0.5 font-[Manrope] font-medium bg-amber-100 text-amber-800">Wypisany z mailingów</span>}
            </div>
            <p className="text-base text-muted-foreground font-[Manrope] mt-2 flex items-center gap-1.5">
              <MapPin className="h-4 w-4" /> {client.region}{client.country ? ` · ${client.country}` : ""}
            </p>
          </div>
          <div className="flex gap-2">
            {(user?.role === "Admin" || user?.role === "Manager") && (
              <Button variant="outline" onClick={() => navigate(`/invoices?client=${client.client_id}`)} data-testid="client-new-invoice-btn">
                <FileText className="h-4 w-4 mr-2" /> Wystaw fakturę
              </Button>
            )}
            <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-detail-btn" className="transition-all duration-200 hover:-translate-y-[1px]">
              <Pencil className="h-4 w-4 mr-2" /> Edit
            </Button>
          </div>
        </div>

        <Tabs defaultValue="overview" className="mt-8">
          <TabsList data-testid="client-tabs">
            <TabsTrigger value="overview" data-testid="tab-overview" className="font-[Manrope]">Overview</TabsTrigger>
            <TabsTrigger value="meetings" data-testid="tab-meetings" className="font-[Manrope]">Meetings</TabsTrigger>
            <TabsTrigger value="workspace" data-testid="tab-workspace" className="font-[Manrope]">Workspace</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="border border-border bg-white rounded-md p-6 space-y-5">
                <h3 className="font-[Outfit] font-semibold tracking-tight">Contact</h3>
                <InfoRow icon={UserIcon} label="Contact Person" value={client.contact_person} />
                <InfoRow icon={Mail} label="Email" value={client.email} />
                <InfoRow icon={Phone} label="Phone" value={client.phone} />
                <InfoRow icon={MapPin} label="Kraj" value={client.country} />
                <InfoRow icon={Building2} label="Adres" value={client.address} />
                <InfoRow icon={Hash} label="Tax ID" value={client.nip} />
                <InfoRow icon={Compass} label="Źródło kontaktu" value={client.source} />
                {(client.country ? client.country === "Polska" : client.region === "Poland") && (
                  <>
                    <InfoRow icon={FileDigit} label="REGON" value={client.regon} />
                    <InfoRow icon={Scale} label="KRS" value={client.krs} />
                  </>
                )}
                <InfoRow icon={Landmark} label="Numer konta" value={client.bank_account} />
                <InfoRow icon={Landmark} label="SWIFT / BIC" value={client.swift_bic} />
                {client.nip && (
                  <div className="pt-1" data-testid="wl-verify-block">
                    <Button variant="outline" size="sm" onClick={verifyWL} disabled={wlLoading} data-testid="verify-wl-btn" className="w-full">
                      <ShieldCheck className="h-4 w-4 mr-2" /> {wlLoading ? "Sprawdzam…" : "Weryfikuj na Białej Liście"}
                    </Button>
                    {wl && (
                      <div className="mt-3 rounded-md border border-border bg-zinc-50 p-3 space-y-2 text-sm font-[Manrope]" data-testid="wl-result">
                        {!wl.found ? (
                          <p className="text-red-600">Nie znaleziono w rejestrze MF.</p>
                        ) : (
                          <>
                            <div className="flex items-center gap-2">
                              <span className="text-zinc-500">Status VAT:</span>
                              <span className={vatCls(wl.status_vat)} data-testid="wl-vat-status">{wl.status_vat || "—"}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-zinc-500">Konto w wykazie:</span>
                              {(client.bank_account || "").trim() ? (
                                wl.account_listed ? (
                                  <span className="text-emerald-700 flex items-center gap-1" data-testid="wl-account-listed"><Check className="h-3.5 w-3.5" /> Tak</span>
                                ) : (
                                  <span className="text-red-600 flex items-center gap-1" data-testid="wl-account-listed"><AlertTriangle className="h-3.5 w-3.5" /> Nie figuruje</span>
                                )
                              ) : (
                                <span className="text-zinc-400">brak numeru konta</span>
                              )}
                            </div>
                            <p className="text-xs text-zinc-400">Sprawdzono: {wl.checked_at.toLocaleString("pl-PL")}</p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <div className="flex items-start gap-3" data-testid="client-subscription">
                  <Star className="h-4 w-4 text-zinc-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs text-zinc-400 font-[Manrope] uppercase tracking-wide">Abonament</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {SUB_STARS[client.subscription] > 0 ? (
                        <span className="flex items-center gap-0.5">
                          {Array.from({ length: SUB_STARS[client.subscription] }).map((_, i) => (
                            <Star key={i} className="h-4 w-4 text-[#C99734] fill-[#C99734]" />
                          ))}
                          <span className="font-[Manrope] text-sm ml-1.5">{client.subscription}</span>
                        </span>
                      ) : (
                        <span className="font-[Manrope] text-sm text-muted-foreground">Nieabonamentowy</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-testid="client-value">
                  <Coins className="h-4 w-4 text-zinc-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs text-zinc-400 font-[Manrope] uppercase tracking-wide">Dochodowość (rocznie)</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="font-[Outfit] font-bold text-lg text-[#193962]">{plnFmt(client.annual_value)}</span>
                      <span className={`rounded-full text-xs px-2 py-0.5 font-[Manrope] font-medium ${gradeOf(client.annual_value || 0).cls}`}>{gradeOf(client.annual_value || 0).label}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="border border-border bg-white rounded-md p-6 lg:col-span-2">
                <h3 className="font-[Outfit] font-semibold tracking-tight mb-4">Notes</h3>
                {client.notes ? (
                  <div
                    data-testid="client-notes-display"
                    className="prose prose-sm max-w-none font-[Manrope] text-sm [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(client.notes) }}
                  />
                ) : (
                  <p className="text-sm text-muted-foreground font-[Manrope]">No notes yet.</p>
                )}
              </div>
            </div>
            <RevenuePanel clientId={client.client_id} canInvoice={user?.role === "Admin" || user?.role === "Manager"} />
          </TabsContent>

          <TabsContent value="meetings" className="mt-6">
            <MeetingsTab client={client} />
          </TabsContent>

          <TabsContent value="workspace" className="mt-6">
            <WorkspaceTab client={client} />
          </TabsContent>
        </Tabs>
      </main>

      <ClientForm open={editOpen} onOpenChange={setEditOpen} client={client} onSaved={load} />
    </div>
  );
}
